import {
  TournamentCompetition,
  TournamentCompetitionEvent,
  TournamentEntry,
  TournamentPlayer,
  TournamentRegistration,
  TournamentSession,
} from "@mixitone/models";
import { modelCopier } from "lib/ModelCopier";

/**
 * ModelCopiers used when duplicating a tournament.
 * `modelCopier` requires included + excluded to name every model field, so a new
 * column is a TypeScript error (`pnpm run tsc`) rather than a runtime crash.
 */
export function createTournamentCopyCopiers() {
  const tournamentSessionCopier = modelCopier(TournamentSession, "TournamentSession", {
    included: [
      "account_id",
      "banner_image_id",
      "club_id",
      "completed_at",
      "confirmation_text",
      "courts",
      "created_by",
      "description",
      "ends_at",
      "is_deleted",
      "is_public",
      "is_test_mode",
      "name",
      "payment_by_player_or_entry",
      "registration_closes_at",
      "stand_down_minutes",
      "starts_at",
      "thank_you_text",
    ],
    excluded: [
      "created_at",
      "updated_at",
      "portal_slug", // Must be unique
      "short_code", // Must be unique; trigger generates a new one
      "status", // Set to "setup" explicitly
      "thank_you_sent_at", // Cleared - thank-you not sent for copy
    ],
  });

  const tournamentPlayerCopier = modelCopier(TournamentPlayer, "TournamentPlayer", {
    included: [
      "account_id",
      "club_id",
      "club_player_id",
      "email",
      "is_deleted",
      "mobile",
      "name",
      "payment_reference",
      "player_profile_id",
      "tournament_payment_method_id",
    ] as const,
    excluded: [
      "created_at",
      "updated_at",
      "tournament_session_id", // Set to new session ID
      "paid_at", // Cleared - players should be unconfirmed
      "confirmed_at", // Cleared - players should be unconfirmed
      "confirmation_sent_at", // Cleared - players should be unconfirmed
      "thank_you_sent_at", // Cleared - thank-you not sent for copy
      "checked_in_at", // Cleared - players should not be checked in
      "withdrawn_at", // Cleared - players should not be withdrawn
    ] as const,
  });

  const tournamentCompetitionCopier = modelCopier(TournamentCompetition, "TournamentCompetition", {
    included: [
      "account_id",
      "banner_image_id",
      "club_id",
      "description",
      "entry_type",
      "format",
      "format_settings",
      "is_deleted",
      "max_entries",
      "min_entries",
      "name",
      "rules",
    ] as const,
    excluded: [
      "created_at",
      "updated_at",
      "tournament_session_id", // Set to new session ID
      "status", // Set to "setup" explicitly
      "format_data", // Cleared - matches will be regenerated
    ] as const,
  });

  const tournamentEntryCopier = modelCopier(TournamentEntry, "TournamentEntry", {
    included: ["account_id", "club_id", "is_deleted", "name", "seed", "withdrawn"] as const,
    excluded: [
      "created_at",
      "updated_at",
      "tournament_competition_id", // Set to new competition ID
      "format_data", // Cleared - matches will be regenerated
      "eliminated", // Set to false explicitly
      "last_match_completed_at", // Cleared
    ] as const,
  });

  const tournamentRegistrationCopier = modelCopier(TournamentRegistration, "TournamentRegistration", {
    included: ["account_id", "club_id", "is_deleted", "partner_code", "partner_email"] as const,
    excluded: [
      "created_at",
      "updated_at",
      "tournament_player_id", // Set to new player ID
      "tournament_competition_id", // Set to new competition ID
      "tournament_entry_id", // Set to new entry ID
      "partner_joined", // Set to false explicitly
      "partner_email_sent_at", // Cleared
      "registration_received_email_sent_at", // Cleared
      "admin_signup_notification_sent_at", // Cleared
      "withdrawn_at", // Cleared
    ] as const,
  });

  const tournamentEventCopier = modelCopier(TournamentCompetitionEvent, "TournamentCompetitionEvent", {
    included: [
      "account_id",
      "club_id",
      "client_id",
      "client_seq",
      "event_type",
      "payload",
      "is_deleted",
    ] as const,
    excluded: [
      "created_at",
      "updated_at",
      "base_event_id", // Event IDs are regenerated during copy
      "tournament_competition_id", // Set to new competition ID
    ] as const,
  });

  return {
    tournamentSessionCopier,
    tournamentPlayerCopier,
    tournamentCompetitionCopier,
    tournamentEntryCopier,
    tournamentRegistrationCopier,
    tournamentEventCopier,
  };
}

export const {
  tournamentSessionCopier,
  tournamentPlayerCopier,
  tournamentCompetitionCopier,
  tournamentEntryCopier,
  tournamentRegistrationCopier,
  tournamentEventCopier,
} = createTournamentCopyCopiers();
